import { Children, isValidElement, useRef } from 'react';
import styled from 'styled-components';
import CarouselItem from './sportsbook-carousel-item';
import { LeftArrow, RightArrow, SliderWrapper } from './sportsbook-carousel-layout';
import Icon, { ICON_LEFT, ICON_RIGHT } from '~/components/icon';
import { useTypedTranslationsWithPartialResult } from '~/components/translation/use-translations';
import CarouselTextKeys from './sportsbook-carousel-text-keys';
import useCarouselScrollState from './hooks/use-carousel-scroll-state';
import useCarouselNavigation from './hooks/use-carousel-navigation';
import useCarouselDrag from './hooks/use-carousel-drag';

const Wrapper = styled.div`
	display: grid;
	grid-template-columns: 1fr;
	position: relative;

	&:hover ${LeftArrow}, &:hover ${RightArrow} {
		opacity: 1;
		pointer-events: auto;
	}
`;

type Props = {
	gap: number;
	paddingX?: number;
	children?: React.ReactNode | React.ReactNode[];
	showArrows: boolean;
};

/**
 * Just another generic carousel component.
 *
 * Takes no consideration to where it is used.
 * Has no built in outside padding or margins.
 * Has no built in item width, render children with the absolute width that is wanted.
 * Has no built in item height, add height: 100% to children if all children should have the same height.
 */
const SportsbookCarousel: React.FC<Props> = ({ gap, paddingX = 0, children, showArrows }) => {
	const { translations } = useTypedTranslationsWithPartialResult(CarouselTextKeys);
	const containerRef = useRef<HTMLDivElement | null>(null);

	const { canScroll, canScrollLeft, canScrollRight, handleScroll } = useCarouselScrollState(
		containerRef,
		Children.count(children)
	);
	const { handleClickNext, handleClickPrevious } = useCarouselNavigation(containerRef, canScroll, paddingX);
	const { isDragging, handleDragStart, handleDrag, handleDragEnd } = useCarouselDrag(containerRef, canScroll);

	return (
		<Wrapper>
			<SliderWrapper
				gap={gap}
				paddingX={paddingX}
				onMouseDown={handleDragStart}
				onMouseMove={handleDrag}
				onMouseUp={handleDragEnd}
				onMouseLeave={handleDragEnd}
				onScroll={handleScroll}
				ref={containerRef}
				isScrollingDisabled={!canScroll}
				$isDragging={isDragging}
				data-body-scroll-lock-ignore // Prevents body scroll lock from locking the scroll see disable-body-scroll.js
			>
				{Children.map(children, child => {
					if (isValidElement<{ key: string }>(child)) {
						return <CarouselItem key={child.key}>{child}</CarouselItem>;
					}

					return null;
				})}
			</SliderWrapper>
			{showArrows && (
				<>
					{canScrollLeft && (
						<LeftArrow
							onClick={handleClickPrevious}
							aria-label={translations[CarouselTextKeys.SCROLL_TO_LEFT]}
						>
							<Icon type={ICON_LEFT} />
						</LeftArrow>
					)}
					{canScrollRight && (
						<RightArrow
							onClick={handleClickNext}
							aria-label={translations[CarouselTextKeys.SCROLL_TO_RIGHT]}
						>
							<Icon type={ICON_RIGHT} />
						</RightArrow>
					)}
				</>
			)}
		</Wrapper>
	);
};

SportsbookCarousel.displayName = 'SportsbookCarousel';

export default SportsbookCarousel;
